import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { getSpeakerLabel, getSpeakerColor } from '../config/participants';

const formatTime = (timestamp: string): string => {
  try {
    let ts = timestamp;
    if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
      ts = `${timestamp}Z`;
    }
    return new Date(ts).toLocaleTimeString(undefined, {
      hour12: true,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '';
  }
};

// Outer className is intentionally NOT set here — the parent (CombinedInfoBar) owns
// the wrapper styling and the `relative` positioning context so the dropdown stretches
// to the full combined-bar width via `absolute left-0 right-0 top-full`.
export const LiveTranscriptBar: React.FC = () => {
  const { transcripts, participants } = useSelector((state: RootState) => state.conversation);
  const [isExpanded, setIsExpanded] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);
  // Tracks the speaker of the latest unread transcript while the bar is collapsed.
  // Cleared when the user opens the panel to view transcripts.
  const [newTranscriptSpeaker, setNewTranscriptSpeaker] = useState<'customer' | 'salesperson' | null>(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const seenCountRef = useRef(0);

  const latestTranscript = transcripts.length > 0 ? transcripts[transcripts.length - 1] : null;

  // Highlight the collapsed bar when new transcripts arrive while it's closed.
  // Badge flashes for 3 pulses (3 × 0.4s = 1.2s) then stays red until user opens.
  useEffect(() => {
    if (!isExpanded && transcripts.length > seenCountRef.current && latestTranscript) {
      const speaker = latestTranscript.speaker as 'customer' | 'salesperson';
      if (speaker === 'customer' || speaker === 'salesperson') {
        setNewTranscriptSpeaker(speaker);
        setIsFlashing(true);
        const timer = setTimeout(() => setIsFlashing(false), 1200);
        return () => clearTimeout(timer);
      }
    }
  }, [transcripts, isExpanded, latestTranscript]);

  // Auto-scroll history to bottom when expanded or new transcripts arrive
  useEffect(() => {
    if (isExpanded && feedRef.current && transcripts.length > 0) {
      requestAnimationFrame(() => {
        if (feedRef.current) {
          feedRef.current.scrollTop = feedRef.current.scrollHeight;
        }
      });
    }
  }, [transcripts, isExpanded]);

  const handleToggle = () => {
    setIsExpanded((prev) => {
      const opening = !prev;
      if (opening) {
        // Mark all current transcripts as seen and clear the new-message indicator
        seenCountRef.current = transcripts.length;
        setNewTranscriptSpeaker(null);
      }
      return opening;
    });
  };

  return (
    <>
      {/* Collapsed bar — always visible.
          When a new transcript arrives while collapsed, background reflects the speaker:
          customer → gray-100, salesperson → obsidian (matches bubble colors in expanded view).
          Color clears as soon as the user opens the panel. */}
      <div
        className={`px-4 py-1 cursor-pointer transition-colors ${
          !isExpanded && newTranscriptSpeaker === 'salesperson'
            ? 'bg-obsidian/10 hover:bg-obsidian/20'
            : !isExpanded && newTranscriptSpeaker === 'customer'
            ? 'bg-gray-100 hover:bg-gray-200'
            : 'hover:bg-gray-50'
        }`}
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0">
            {/* Label */}
            <div className="flex items-center space-x-1 flex-shrink-0">
              <span className="text-sm">🎤</span>
              <h3 className={`text-xs font-semibold ${
                !isExpanded && newTranscriptSpeaker === 'salesperson' ? 'text-obsidian' : 'text-gray-800'
              }`}>
                Transcript
              </h3>
              <span className={`px-1.5 py-0 text-xs rounded-full border font-medium ${
                !isExpanded && newTranscriptSpeaker
                  ? `bg-red-500 text-white border-red-600${isFlashing ? ' animate-badge-flash' : ''}`
                  : transcripts.length > 0
                  ? 'bg-green-100 text-green-800 border-green-300'
                  : 'bg-gray-100 text-gray-600 border-gray-300'
              }`}>
                {transcripts.length}
              </span>
            </div>

            {/* Latest entry preview */}
            {latestTranscript ? (
              <div className="flex items-center space-x-1 text-xs min-w-0">
                <span className={`flex-shrink-0 ${
                  !isExpanded && newTranscriptSpeaker === 'salesperson' ? 'text-obsidian' : 'text-gray-400'
                }`}>
                  {getSpeakerLabel(latestTranscript, participants as any, { salespersonLabel: 'You' })}:
                </span>
                <span className={`truncate ${
                  !isExpanded && newTranscriptSpeaker === 'salesperson' ? 'text-obsidian' : 'text-gray-600'
                }`}>
                  {latestTranscript.text.length > 60
                    ? `${latestTranscript.text.substring(0, 60)}\u2026`
                    : latestTranscript.text}
                </span>
              </div>
            ) : (
              <span className="text-xs text-gray-400">No transcripts yet</span>
            )}
          </div>

          {/* Expand / collapse chevron */}
          <button
            className={`transition-colors p-0.5 flex-shrink-0 ml-2 ${
              !isExpanded && newTranscriptSpeaker === 'salesperson'
                ? 'text-obsidian/70 hover:text-obsidian'
                : 'text-gray-400 hover:text-gray-600'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
          >
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded dropdown — positioned via absolute on the CombinedInfoBar's relative container */}
      {isExpanded && (
        <div className="absolute left-0 right-0 top-full border-t border-gray-200 bg-gray-50 shadow-lg z-50">
          <div
            ref={feedRef}
            className="px-2 py-2 max-h-[50vh] overflow-y-auto bg-white"
            style={{ scrollBehavior: 'smooth' }}
          >
            {transcripts.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <div className="text-4xl mb-2">🎤</div>
                <p className="text-sm">No transcripts yet</p>
                <p className="text-xs">Start speaking to see the live transcript</p>
              </div>
            ) : (
              <div>
                {transcripts.map((transcript) => {
                  const isCustomer = transcript.speaker === 'customer';
                  // Per-customer name + stable palette color (multi-party).
                  const label = getSpeakerLabel(transcript, participants as any, { salespersonLabel: 'Salesperson' });
                  const icon = isCustomer ? '🙋‍♂️' : '🧑‍💼';
                  // Salesperson keeps the branded obsidian bubble; customers get
                  // their palette color from the shared helper.
                  const bubbleStyle = isCustomer
                    ? (() => {
                        const c = getSpeakerColor(transcript, participants as any);
                        return { backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` };
                      })()
                    : undefined;
                  return (
                    <div
                      key={transcript.id}
                      className={`flex ${isCustomer ? 'justify-start' : 'justify-end'} mb-1`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-2 py-1 rounded-lg ${
                          isCustomer ? '' : 'bg-obsidian text-bone'
                        }`}
                        style={bubbleStyle}
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-medium">
                            {icon} {label}
                          </span>
                          <span className="text-xs opacity-75 ml-2">
                            {formatTime(transcript.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm">{transcript.text}</p>
                      </div>
                    </div>
                  );
                })}
                <div className="h-1" />
              </div>
            )}
          </div>

          {transcripts.length > 0 && (
            <div className="px-6 py-2 border-t border-gray-200 bg-white text-center">
              <span className="text-sm text-gray-600">
                {transcripts.length} transcript{transcripts.length === 1 ? '' : 's'} recorded
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
};
